import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
console.log('[DEBUG] ChargebeeMapping loaded');
console.log('[DEBUG] API_BASE_URL:', API_BASE_URL);

async function fetchJsonSafe(url) {
  const res = await fetch(url);
  const contentType = res.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return await res.json();
  } else {
    const text = await res.text();
    throw new Error(`API returned non-JSON from ${url}: ${text.slice(0, 100)}`);
  }
}

export default function ChargebeeMapping() {
  const [programs, setPrograms] = useState([]);
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMapping, setEditMapping] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        const [programsRes, plansRes, addonsRes, mappingsRes] = await Promise.all([
          fetchJsonSafe(`${API_BASE_URL}/api/programs`),
          fetchJsonSafe(`${API_BASE_URL}/api/plans`),
          fetchJsonSafe(`${API_BASE_URL}/api/addons`),
          fetchJsonSafe(`${API_BASE_URL}/api/mappings`)
        ]);
        console.log('[DEBUG] Programs:', programsRes);
        console.log('[DEBUG] Plans:', plansRes);
        console.log('[DEBUG] Addons:', addonsRes);
        console.log('[DEBUG] Mappings:', mappingsRes);
        setPrograms(programsRes);
        setPlans(plansRes);
        setAddons(addonsRes);
        setMappings(mappingsRes);
      } catch (err) {
        alert('Error loading data: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const handleSelect = (programCode, value, type) => {
    setEditMapping({ ...editMapping, [programCode]: { chargebeeId: value, type } });
  };

  const handleSave = async (programCode) => {
    setSaving(true);
    const mapping = {
      programCode,
      chargebeeId: editMapping[programCode]?.chargebeeId || '',
      type: editMapping[programCode]?.type || '',
      manual: true
    };
    await fetch(`${API_BASE_URL}/api/mappings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(mapping)
    });
    // Refresh mappings
    const mappingsRes = await fetchJsonSafe(`${API_BASE_URL}/api/mappings`);
    setMappings(mappingsRes);
    setSaving(false);
  };

  // =====================
  // Customer Sync Handler (TEST CODE, COMMENTED OUT)
  // const [syncCustomers, setSyncCustomers] = useState([]);
  // const [syncing, setSyncing] = useState(false);
  // const [syncResult, setSyncResult] = useState(null);
  // const handleCustomerSync = async () => {
  //   setSyncing(true);
  //   setSyncResult(null);
  //   setSyncCustomers([]);
  //   try {
  //     const res = await fetch(`${API_BASE_URL}/api/customers/search`, {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify({
  //         records: 50,
  //         offset: 0,
  //         customerStatus: { minValue: '9 - Active', maxValue: '9 - Active' }
  //       })
  //     });
  //     if (!res.ok) throw new Error(`Sync failed: ${res.status}`);
  //     const data = await res.json();
  //     const customers = Array.isArray(data) ? data : (data.results || data.customers || []);
  //     const trimmed = customers.map(c => ({
  //       firstName: c.customerFirstName,
  //       lastName: c.customerLastName,
  //       route: c.customerRoute,
  //       street: c.customerStreetAddress,
  //       city: c.customerCity,
  //       state: c.customerState,
  //       zip: c.customerZip,
  //       size: c.customerSize
  //     }));
  //     setSyncCustomers(trimmed);
  //     setSyncResult({ success: true, message: `Fetched ${trimmed.length} customers.` });
  //   } catch (err) {
  //     setSyncResult({ success: false, message: err.message });
  //   } finally {
  //     setSyncing(false);
  //   }
  // };
  // =====================

  if (loading) return <div>Loading...</div>;

  console.log('[DEBUG] Render - programs:', programs, 'plans:', plans, 'addons:', addons, 'mappings:', mappings);

  return (
    <Box sx={{ p: 4, background: '#f0f0f0', borderRadius: 2, mt: 2 }}>
      {/*
      <Button onClick={handleCustomerSync} disabled={syncing}>
        SYNC CUSTOMERS TO CHARGEBEE
      </Button>
      {syncResult && (
        <Alert severity={syncResult.success ? "success" : "error"}>
          {syncResult.message}
        </Alert>
      )}
      {syncCustomers.length > 0 && (
        <div>
          <p>Showing first 10 of {syncCustomers.length} customers:</p>
          <Table>
            // ...table head and body mapping syncCustomers...
          </Table>
        </div>
      )}
      */}
      <Typography variant="h5" color="primary" gutterBottom>
        Chargebee Mapping Module
      </Typography>
      <TableContainer component={Paper} sx={{ mt: 2, overflowX: 'auto', boxShadow: 2 }}>
        <Table sx={{ minWidth: 700 }} size="small" aria-label="chargebee mapping table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>RealGreen Program</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Mapped Chargebee Plan/Addon</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Manual?</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Change Mapping</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {programs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ color: 'red', textAlign: 'center' }}>
                  No programs found. Check API connectivity and REACT_APP_API_BASE_URL.
                </TableCell>
              </TableRow>
            ) : (
              programs.map(program => {
                const mapping = mappings.find(m => m.programCode === program.programCode);
                const mappedId = mapping?.chargebeeId || '';
                const isManual = mapping?.manual || false;
                return (
                  <TableRow key={program.programCode}>
                    <TableCell>{program.description}</TableCell>
                    <TableCell>
                      {mappedId
                        ? (plans.find(p => p.id === mappedId)?.name || addons.find(a => a.id === mappedId)?.name || mappedId)
                        : <span style={{color:'#888'}}>No mapping</span>
                      }
                    </TableCell>
                    <TableCell>{isManual ? 'Yes' : 'No'}</TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={editMapping[program.programCode]?.chargebeeId || mappedId || ''}
                        onChange={e => {
                          const val = e.target.value;
                          const isPlan = plans.some(p => p.id === val);
                          handleSelect(program.programCode, val, isPlan ? 'plan' : 'addon');
                        }}
                        disabled={saving}
                        sx={{ minWidth: 180, mr: 1 }}
                      >
                        <MenuItem value=''>-- Select --</MenuItem>
                        <optgroup label="Plans">
                          {plans.map(plan => (
                            <MenuItem key={plan.id} value={plan.id}>{plan.name}</MenuItem>
                          ))}
                        </optgroup>
                        <optgroup label="Addons">
                          {addons.map(addon => (
                            <MenuItem key={addon.id} value={addon.id}>{addon.name}</MenuItem>
                          ))}
                        </optgroup>
                      </Select>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleSave(program.programCode)}
                        disabled={saving || !editMapping[program.programCode]?.chargebeeId}
                        sx={{ minWidth: 60 }}
                      >Save</Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography sx={{marginTop:2, color:'#888', fontSize: 14}}>
        (Now interactive! Change a mapping and click Save.)
      </Typography>
    </Box>
  );
}
